import * as React from 'react'
import * as H from 'history'

import { Site } from '@envato/foundation-types'

import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Socket } from 'phoenix-socket'
import flatten from 'lodash-es/flatten'

import { reportSearch } from '../../../common/analytics/searchBarAnalytics'
import autoSuggestEndpoint from '../../../common/constants/autoSuggestEndpoints'
import { WebSocketError, logError } from '../../../common/utils/errors'
import SearchBarForm from '../searchBar/searchBarForm'
import { getTopCategoryLabel } from '../../../config/sites/categories'
import RecentSearch from './recentSearch'
import pushHistory from '../../../common/utils/location/pushHistory'
import redirectSearchIfItemId from '../../../common/utils/location/redirectSearchIfItemId'

import { ILocationParams } from '../../../common/reducers/reactRouterLocation'
import { sanitizeSearchTerm } from '../../../common/utils/sanitize'
import { MakeSearchLink } from '../../../common/utils/location/types'

import { ISuggestion } from './index'

interface IState {
  inputValue: string
  suggestions: ISuggestion[]
}

type PropsType = RouteComponentProps & {
  inputName: string
  placeholder: string
  locationParams: ILocationParams
  makeSearchLink: MakeSearchLink
  currentSite: Site
  value: string
  history: H.History
  showCategories?: boolean
  size?: string
  styles?: { [key: string]: string }
}

let socketConnection: any = null
let socketChannel: any = null

export default (AutoSuggest: React.ComponentType) => {
  class AutoSuggestDataContainer extends React.Component<PropsType, IState> {
    static defaultProps = {
      placeholder: ''
    }

    constructor(props: PropsType) {
      super(props)
      this.state = {
        suggestions: [],
        inputValue: props.value || ''
      }

      this.setSuggestions = this.setSuggestions.bind(this)
      this.onInputChange = this.onInputChange.bind(this)
      this.onSelectOption = this.onSelectOption.bind(this)
      this.onInputFocus = this.onInputFocus.bind(this)
      this.onJoined = this.onJoined.bind(this)
      this.onNewMsg = this.onNewMsg.bind(this)
    }

    componentDidMount() {
      this.initSocket()
    }

    componentWillUnmount() {
      socketChannel.off('joined', this.onJoined)
      socketChannel.off('new_msg', this.onNewMsg)
    }

    UNSAFE_componentWillReceiveProps(nextProps: PropsType) {
      if (nextProps.value !== this.props.value) {
        this.setState({
          inputValue: nextProps.value
        })
      }
    }

    onJoined() {
      socketChannel
        .receive('ok', () => {
          if (this.state.inputValue) {
            this.sendMessage(this.state.inputValue)
          }
        })
        .receive('error', (reason: string) => {
          logError(WebSocketError({ message: reason }))
        })
    }

    onNewMsg(message: { body: ISuggestion[] }) {
      this.setSuggestions(message.body)
    }

    initSocket() {
      const { endpoint, channel } = autoSuggestEndpoint

      if (!socketConnection) {
        socketConnection = new Socket(endpoint)
        socketChannel = socketConnection.channel(channel, {})
        socketConnection.connect()

        socketChannel.join()
      }

      socketChannel.on('joined', this.onJoined)
      socketChannel.on('new_msg', this.onNewMsg)
    }

    sendMessage(value = '') {
      const { currentSite } = this.props

      // trim spaces from terms before sending
      // to ES to prevent serverside errors
      const searchString = sanitizeSearchTerm(value).toLowerCase()

      const message = {
        body: searchString,
        category: null,
        site: currentSite || 'themeforest'
      }

      if (
        socketConnection &&
        socketChannel &&
        socketConnection.isConnected() &&
        socketChannel.isJoined()
      ) {
        socketChannel
          .push('new_msg', message)
          .receive('error', (reason: string) => {
            logError(WebSocketError({ message: reason }))
          })
      }
    }

    setSuggestions(suggestions: ISuggestion[]) {
      this.setState({
        suggestions
      })
    }

    onInputChange(value = '') {
      const { makeSearchLink } = this.props
      this.setState({
        inputValue: value
      })
      if (value === '') {
        this.setSuggestions(RecentSearch.getRecentSearch({}, makeSearchLink))
      } else {
        this.sendMessage(value)
      }
    }

    getLink(term: string) {
      const { locationParams, makeSearchLink } = this.props

      const nextQueries = Object.assign({}, locationParams, {
        term
      })
      // reset page number
      delete nextQueries.page
      // reset sort
      delete nextQueries.sort_by

      return makeSearchLink(nextQueries)
    }

    getCategoryLink(category: string, term: string) {
      const { locationParams, makeSearchLink } = this.props

      const nextQueries = Object.assign({}, locationParams, {
        category,
        term
      })
      // reset page number
      delete nextQueries.page
      // reset sort
      delete nextQueries.sort_by

      return makeSearchLink(nextQueries)
    }

    async onSelectOption(
      option: ISuggestion | string,
      event: React.KeyboardEvent | React.MouseEvent
    ) {
      const { history } = this.props

      let nextOption: { text: string; link: string } | null

      // if the selection option is a string, we need to build the link
      if (typeof option === 'string') {
        if (redirectSearchIfItemId(option)) {
          return
        }
        nextOption = {
          text: option,
          link: this.getLink(option)
        }
        // if the selection option is an object, it means it's triggered by auto-suggest
        // we need to report this to ga
      } else if (typeof option === 'object') {
        if (redirectSearchIfItemId(option.text)) {
          return
        }
        nextOption = Object.assign({}, option)
        reportSearch('autosuggest')
      } else {
        nextOption = null
      }

      if (nextOption && typeof nextOption.text === 'string') {
        if (nextOption.text !== '') {
          RecentSearch.addItem(nextOption.text)
        }

        this.setState(
          {
            inputValue: nextOption.text
          },
          () => {
            if (nextOption) {
              pushHistory(history, nextOption.link)
            }
          }
        )
      }
    }

    onInputFocus(value = '') {
      const { locationParams, makeSearchLink } = this.props
      if (value === '') {
        this.setState({
          suggestions: RecentSearch.getRecentSearch(
            locationParams,
            makeSearchLink
          )
        })
      }
    }

    getCategorySuggestions() {
      const { suggestions } = this.state
      const { currentSite } = this.props

      const categorySuggestions = suggestions
        .filter(suggestion => suggestion.type === 'c')
        .map(suggestion => {
          return suggestion.categories.map((categoryKey: string) => ({
            link: this.getCategoryLink(categoryKey, suggestion.text),
            type: suggestion.type,
            text: suggestion.text,
            category:
              getTopCategoryLabel(currentSite, categoryKey) || categoryKey
          }))
        })

      return flatten(categorySuggestions)
    }

    getSuggestions() {
      const { showCategories } = this.props
      const { suggestions } = this.state

      const nonCategorySuggestions = suggestions
        .filter(suggestion => suggestion.type !== 'c')
        .map(suggestion => {
          suggestion.link = this.getLink(suggestion.text)
          return suggestion
        })

      if (showCategories) {
        return this.getCategorySuggestions().concat(nonCategorySuggestions)
      }

      return nonCategorySuggestions
    }

    render() {
      const { inputValue } = this.state
      const { locationParams, makeSearchLink } = this.props

      const autosuggestProps = {
        ...this.props,
        suggestions: this.getSuggestions(),
        onInputChange: this.onInputChange,
        onSelectOption: this.onSelectOption,
        inputValue,
        type: 'search',
        onInputFocus: this.onInputFocus
      }
      return (
        <SearchBarForm
          locationParams={locationParams}
          makeSearchLink={makeSearchLink}
        >
          <AutoSuggest {...autosuggestProps} />
        </SearchBarForm>
      )
    }
  }

  return withRouter(AutoSuggestDataContainer)
}
